<template>
    <div class="box p-3 postal-code-search-popup">
        <div v-if="isLoading" class="has-text-centered">
            <span class="button is-loading is-small">Loading</span>
        </div>
        <div v-else>
            <!-- Header with title -->
            <div class="mb-1">
                <h4 class="title is-6 mb-0">Postal Code: {{ addressComponents?.postalCode || 'N/A' }}</h4>
            </div>

            <!-- Zone Information Section -->
            <div class="box p-2 has-background-light mb-1">
                <h5 class="title is-6 mb-2">Zone Information</h5>
                <div class="columns is-multiline is-mobile">
                    <div class="column is-half">
                        <div class="field mb-1">
                            <label class="label is-small mb-0">Distribution Zone</label>
                            <p class="is-size-7 m-0">{{ zoneInfo?.distributionZone || 'N/A' }}</p>
                        </div>
                    </div>
                    <div class="column is-full m-0">
                        <hr class="m-0">
                    </div>
                    <div class="column is-full">
                        <div class="field mb-1">
                            <label class="label is-small mb-0">Billable Zones</label>
                            <div v-if="zoneInfo?.billableZones?.length" class="tags-container">
                                <span v-for="zone in zoneInfo.billableZones" :key="zone" class="tag-item">{{ zone
                                }}</span>
                            </div>
                            <p v-else class="is-size-7 m-0">N/A</p>
                        </div>
                    </div>
                    <div class="column is-full">
                        <div class="field mb-1">
                            <label class="label is-small mb-0">Clients</label>
                            <div v-if="zoneInfo?.vendors?.length" class="tags-container">
                                <span v-for="vendor in zoneInfo.vendors" :key="vendor" class="tag-item">{{ vendor
                                }}</span>
                            </div>
                            <p v-else class="is-size-7 m-0">N/A</p>
                        </div>
                    </div>
                    <div class="column is-full m-0">
                        <hr class="m-0">
                    </div>
                    <div class="column is-full">
                        <div class="field mb-1">
                            <label class="label is-small mb-0">Payable Zones</label>
                            <div v-if="zoneInfo?.payableZones?.length" class="tags-container">
                                <span v-for="zone in zoneInfo.payableZones" :key="zone" class="tag-item">{{ zone
                                }}</span>
                            </div>
                            <p v-else class="is-size-7 m-0">N/A</p>
                        </div>
                    </div>
                    <div class="column is-full">
                        <div class="field mb-0">
                            <label class="label is-small mb-0">Contractors</label>
                            <div class="scrollable-list">
                                <div v-if="zoneInfo?.contractors?.length" class="tags-container">
                                    <span v-for="contractor in zoneInfo.contractors" :key="contractor"
                                        class="tag-item">{{ contractor }}</span>
                                </div>
                                <p v-else class="is-size-7 m-0">N/A</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Days of Service Section -->
            <div class="box p-2 has-background-light mb-1 days-of-service">
                <h5 class="title is-6 mb-2">Days of Service</h5>
                <div class="columns is-multiline is-mobile">
                    <div class="column is-one-third" v-for="(day, index) in daysOfService" :key="index">
                        <div class="field m-0">
                            <label class="checkbox">
                                <input type="checkbox" :checked="day.enabled" @change="toggleDay(index)"
                                    :disabled="isSaving" />
                                <span class="ml-2">{{ day.name }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Message notifications -->
            <div v-if="message" class="notification is-small mb-1" :class="messageClass">
                {{ message }}
            </div>
            <!-- Action buttons -->
            <div class="field is-grouped is-grouped-right mt-1">
                <div class="control">
                    <button class="button is-small is-success" :class="{ 'is-loading': isSaving }"
                        style="border-radius: 5px" :disabled="isSaving" @click="handleSave">
                        Save
                    </button>
                </div>
                <div class="control">
                    <button class="button is-small" :disabled="isSaving" style="border-radius: 5px"
                        @click="handleClose">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getPostalCode, updateOne } from '../store/mongoStore'

/**
 * Popup component for postal code search results
 * Displays zone information and allows editing of days of service
 */
export default {
    name: 'PostalCodeSearchPopup',

    props: {
        store: {
            type: Object,
            default: () => null
        },
        /**
         * Geocode data for the selected location
         */
        addressComponents: {
            type: Object,
            default: () => null
        },
        /**
         * List of available clients
         */
        clients: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            isLoading: false,
            isSaving: false,
            message: '',
            postalCode: this.addressComponents?.postalCode || '',
            hub: null,
            zoneInfo: {
                distributionZone: null,
                region: null,
                billableZones: [],
                payableZones: [],
                vendors: [],
                contractors: []
            },
            daysOfService: [
                { name: 'Monday', enabled: false, key: 'mon' },
                { name: 'Tuesday', enabled: false, key: 'tue' },
                { name: 'Wednesday', enabled: false, key: 'wed' },
                { name: 'Thursday', enabled: false, key: 'thu' },
                { name: 'Friday', enabled: false, key: 'fri' },
                { name: 'Saturday', enabled: false, key: 'sat' },
                { name: 'Sunday', enabled: false, key: 'sun' }
            ],
            postalCodeData: null,
            rateTableZones: {}
        };
    },

    mounted() {
        this.loadPostalCodeData();
    },

    computed: {
        /**
         * Determines the appropriate CSS class for message notifications
         */
        messageClass() {
            if (!this.message) return '';
            return this.message.includes('Error') ? 'is-danger' : 'is-success';
        }
    },

    methods: {
        /**
         * Loads postal code data including zone information and schedule from MongoDB
         */
        async loadPostalCodeData() {
            this.isLoading = true;
            this.message = '';

            const postalCodeSearch = this.postalCode
                ?.trim()
                ?.replace(/\s/g, "") || "";

            try {
                // Get all postal code data from MongoDB
                const result = await getPostalCode(postalCodeSearch);
                if (result?.length > 0) {
                    this.postalCodeData = result[0];
                    await this.processPostalCodeData();
                } else {
                    this.message = 'Postal code not found in database.';
                }
            } catch (error) {
                console.error('Error loading postal code data:', error);
                this.message = 'Error loading postal code information.';
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Processes the postal code data to extract zones and schedule information
         */
        async processPostalCodeData() {
            const env = process.env.VUE_APP_SHYFTBASE_CUSTOMER_ID;
            const data = this.postalCodeData;

            // Get distribution zone
            const distributionZoneId = data?.hubs?.[env]?.zone;
            if (distributionZoneId) {
                const storeHubs = this.store?.state?.hubs;
                const hub = storeHubs?.find((hub) => hub.DistributionZoneId === distributionZoneId);
                if (hub) {
                    this.hub = hub;
                    this.zoneInfo.distributionZone = hub.DistributionZoneName || 'N/A';
                }
            }

            // Get days of service
            const daysOfServiceData = data?.hubs?.[env]?.daysOfService;
            if (daysOfServiceData) {
                this.updateDaysOfServiceFromData(daysOfServiceData);
            }

            // Get billable and payable zones
            const zonesData = data?.zones?.[env];
            if (zonesData) {
                await this.processZonesData(zonesData);
            }
        },

        /**
         * Updates days of service based on MongoDB data
         */
        updateDaysOfServiceFromData(daysOfServiceData) {
            this.daysOfService.forEach(day => {
                day.enabled = daysOfServiceData[day.key] || false;
            });
        },

        /**
         * Processes zones data to extract billable and payable zones
         */
        async processZonesData(zonesData) {
            const billableZoneIds = new Set();
            const payableZoneIds = new Set();
            const vendorIds = new Set();
            const contractorIds = new Set();

            // Extract unique zone IDs and vendor/contractor IDs
            Object.entries(zonesData).forEach(([key, zoneData]) => {
                if (zoneData?.billableZone) {
                    billableZoneIds.add(zoneData.billableZone);
                }
                if (zoneData?.payableZone) {
                    payableZoneIds.add(zoneData.payableZone);
                }

                // Extract vendor IDs from CLIENT-{vendor id} keys
                if (key.startsWith('CLIENT-')) {
                    const vendorId = key.replace('CLIENT-', '');
                    vendorIds.add(vendorId);
                }

                // Extract contractor IDs from CONTRACTOR-{contractor id} keys
                if (key.startsWith('CONTRACTOR-')) {
                    const contractorId = key.replace('CONTRACTOR-', '');
                    contractorIds.add(contractorId);
                }
            });

            // Load zone names from Firestore
            await this.loadZoneNames(Array.from(billableZoneIds), Array.from(payableZoneIds));

            // Map vendor and contractor IDs to names
            this.mapVendorAndContractorNames(Array.from(vendorIds), Array.from(contractorIds));
        },

        /**
         * Loads zone names from store state
         */
        async loadZoneNames(billableZoneIds, payableZoneIds) {
            try {
                const billingZoneInformations = this.store.state.zoneInformations.filter(zone => zone.type === "billable").reduce((acc, zone) => {
                    acc[zone.id] = zone.key;
                    return acc;
                }, {});
                const payableZoneInformations = this.store.state.zoneInformations.filter(zone => zone.type === "payable").reduce((acc, zone) => {
                    acc[zone.id] = zone.key;
                    return acc;
                }, {});

                // Update zone info with lists of unique zone names
                this.zoneInfo.billableZones = Array.from(billableZoneIds)
                    .map(id => billingZoneInformations[id] || id)
                    .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates

                this.zoneInfo.payableZones = Array.from(payableZoneIds)
                    .map(id => payableZoneInformations[id] || id)
                    .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates
            } catch (error) {
                console.error('Error loading zone names:', error);
                this.zoneInfo.billableZones = ['Error loading'];
                this.zoneInfo.payableZones = ['Error loading'];
            }
        },

        /**
         * Maps vendor and contractor IDs to their names using store state
         */
        mapVendorAndContractorNames(vendorIds, contractorIds) {
            const vendors = this.store?.state?.vendors || [];
            const contractors = this.store?.state?.contractors || [];

            // Create mapping for vendors
            const vendorMap = {};
            vendors.forEach(vendor => {
                vendorMap[vendor.Id] = vendor.businessUnit === vendor.subBusinessUnit ? vendor.businessUnit : vendor.businessUnit + ' - ' + vendor.subBusinessUnit;
            });

            // Create mapping for contractors
            const contractorMap = {};
            contractors.forEach(contractor => {
                contractorMap[contractor.id] = contractor.company_name || "";
            });
            console.log("contractorMap", contractors);

            // Update zone info with vendor and contractor names
            this.zoneInfo.vendors = vendorIds
                .map(id => vendorMap[id] || `Vendor ID: ${id}`)
                .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates

            this.zoneInfo.contractors = contractorIds
                .map(id => contractorMap[id] || `Contractor ID: ${id}`)
                .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates
        },

        /**
         * Toggles a specific day of service
         */
        toggleDay(index) {
            if (this.daysOfService[index]) {
                this.daysOfService[index].enabled = !this.daysOfService[index].enabled;
            }
        },

        /**
         * Saves the schedule data
         */
        async handleSave() {
            this.isSaving = true;
            try {
                // Save schedule data if modified
                if (this.postalCodeData) {
                    const env = process.env.VUE_APP_SHYFTBASE_CUSTOMER_ID;
                    const scheduleUpdateData = {};
                    this.daysOfService.forEach(day => {
                        scheduleUpdateData[day.key] = day.enabled;
                    });

                    // Find the postal code document and update only the daysOfService field
                    await updateOne(
                        "postal_codes",
                        { "properties.Postcode": this.postalCode },
                        {
                            $set: {
                                [`hubs.${env}.daysOfService`]: scheduleUpdateData
                            }
                        }
                    );

                    console.log('Days of service updated successfully:', scheduleUpdateData);
                }

                this.message = 'Schedule saved successfully!';

                // Emit save event with the saved data
                // this.$emit('save', {
                //     postalCode: this.postalCode,
                //     schedule: this.daysOfService,
                //     postalCodeData: this.postalCodeData
                // });
            } catch (error) {
                console.error('Error saving schedule data:', error);
                this.message = 'Error saving data. Please try again.';
            }
            this.isSaving = false;
        },

        /**
         * Handles closing the popup
         */
        handleClose() {
            // Emit close event to parent component
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
.postal-code-search-popup {
    width: 500px !important;
    max-width: 500px !important;
    min-width: 500px !important;
    min-height: 400px;
}

.notification.is-small {
    padding: 0.5rem;
    font-size: 0.85rem;
}

/* Add styling for the sections */
.box.p-2 {
    margin-bottom: 0.5rem;
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
}

/* Style for checkboxes */
.checkbox {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
}

.checkbox input[type="checkbox"] {
    margin-right: 0.5rem;
    accent-color: #000000;
}

.checkbox input[type="checkbox"]:checked {
    background-color: #000000;
    border-color: #000000;
}

/* Compact spacing for days of service */
.days-of-service .field.mb-1 {
    margin-bottom: 0rem !important;
}

.days-of-service .column {
    padding-left: 0.1rem;
    padding-right: 0.1rem;
}

.days-of-service .columns.is-multiline.is-mobile {
    margin-left: -0.1rem;
    margin-right: -0.1rem;
}

/* Style for zone lists */
ul {
    list-style-type: disc;
    margin: 0;
    padding-left: 1rem;
}

li {
    margin-bottom: 0;
}

/* Tags container and styling */
.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: flex-start;
    margin: 0;
    max-height: 120px;
    overflow-y: auto;
}

.tag-item {
    display: inline-block;
    background-color: #000000;
    color: #ffffff;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Compact spacing */
.field.mb-1 {
    margin-bottom: 0.25rem !important;
}

.field.mb-0 {
    margin-bottom: 0 !important;
}

.title.is-6.mb-1 {
    margin-bottom: 0.25rem !important;
}

.box.mb-1 {
    margin-bottom: 0.5rem !important;
}

/* Reduce padding in boxes */
.box.p-2 {
    padding: 0.75rem !important;
}

/* Reduce spacing between columns */
.columns.is-multiline.is-mobile {
    margin-left: -0.25rem;
    margin-right: -0.25rem;
}

.column {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}

/* Style for horizontal line */
hr {
    background-color: #ffffff;
    height: 1px;
    border: none;
    margin: 0.5rem 0;
}
</style>
